import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppIdeaRequest, AppIdeaResearchData } from '@/types/app-ideas-types';

interface SubmissionData {
  request: AppIdeaRequest;
  title: string;
  description: string;
  research: AppIdeaResearchData;
}

const submitAppIdea = async ({ request, title, description, research }: SubmissionData) => {
  const { data: { session } } = await supabase.auth.getSession();
  const user_id = session?.user?.id;

  if (!user_id) {
    throw new Error("User not authenticated. Please log in to submit an app idea.");
  }

  console.log('[AppIdeaSubmission] Submitting app idea with research data:', {
    research,
    hasCitations: !!research.citations,
    citationsCount: research.citations?.length || 0,
    hasRelatedQuestions: !!research.relatedQuestions,
    relatedQuestionsCount: research.relatedQuestions?.length || 0
  });

  const { data, error } = await supabase
    .from('app_ideas')
    .insert({
      original_problem: request.problem,
      category: request.category,
      frequency: request.frequency,
      current_solution: request.currentSolution,
      priority: request.priority,
      sharing: request.sharing,
      platform: request.platform,
      additional_details: request.additionalDetails,
      generated_title: title,
      generated_description: description,
      research_overview: research.overview,
      research_requirements: research.core_requirements,
      research_enhanced_features: research.enhanced_features,
      research_citations: research.citations || null,
      research_related_questions: research.relatedQuestions || null,
      title_generation_status: 'completed',
      description_generation_status: 'completed',
      research_generation_status: 'completed',
      processing_status: 'completed',
      status: 'under_review',
      user_id,
      completed_at: new Date().toISOString(),
    })
    .select('id')
    .single();

  console.log('[AppIdeaSubmission] Database insertion result:', { data, error });

  if (error) {
    console.error('[AppIdeaSubmission] Database error:', error);
    throw new Error(`Database submission failed: ${error.message}`);
  }
  return data.id;
};

export const useAppIdeaSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitAppIdea,
    onSuccess: (ideaId) => {
      console.log('[AppIdeaSubmission] Successfully submitted app idea:', ideaId);
      // Invalidate and refetch app ideas to ensure the new idea appears immediately
      queryClient.invalidateQueries({ queryKey: ['app-ideas'] });
      queryClient.invalidateQueries({ queryKey: ['app-idea-favorites'] });
    },
    onError: (error) => {
      console.error('[AppIdeaSubmission] Submission failed:', error);
    }
  });
};
